/**
 * Kit sidebar — keputusan tampilan Owner 24 Sep 2026 (VONIS-24SEP-SORE butir 7–10).
 *
 * Versi A (bawaan): baris rapat · kelompok bisa dilipat (yang terbuka otomatis hanya kelompok
 * halaman aktif; buka/tutup diingat per user; jumlah menu kecil di samping judul) · "Disematkan"
 * (☆) dan "Terakhir dibuka" di atas · "Cari menu" Ctrl+K (nama menu + nama kelompok).
 * Versi B (tombol ciutkan): hanya ikon kelompok; arahkan kursor / tap ikon → daftar menu kelompok
 * muncul di sampingnya. Pilihan ciut/lebar diingat per user.
 * Tombol panah atas: pojok kanan bawah, muncul sesudah halaman digulir jauh.
 *
 * ⛔ TIDAK ADA daftar menu kedua di sini. Sumber kebenarannya tetap JSX `<NavItem>` di Layout.jsx
 *    bersama gerbang perannya: tiap butir yang BENAR-BENAR dirender mendaftarkan diri ke konteks
 *    ini. Disematkan/Terakhir dibuka/Cari/rail Versi B dibangun dari daftar itu, jadi mereka tidak
 *    pernah menampilkan menu yang tidak boleh dilihat peran itu — dan tidak ada gerbang peran yang
 *    disalin (salinan gerbang adalah tempat hak akses diam-diam melebar).
 *
 * Penyimpanan preferensi: localStorage per user (kenyamanan per perangkat, bukan data); semua akses
 * dibungkus try/catch — mode privat atau penyimpanan diblokir tidak boleh merusak navigasi.
 */
import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { posisiPanel } from "./posisiPanel";
import { bolehTampil } from "./zonaTombolKeAtas";
import { ChevronRight, Star, Search, PanelLeftClose, PanelLeftOpen, ArrowUp, Clock } from "lucide-react";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "../ui/command";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const NavCtx = React.createContext(null);
const MAKS_TERAKHIR = 5;

function bacaPrefs(kunci) {
  try {
    const t = window.localStorage.getItem(kunci);
    return t ? JSON.parse(t) : {};
  } catch (_e) {
    return {};
  }
}

function tulisPrefs(kunci, nilai) {
  try {
    window.localStorage.setItem(kunci, JSON.stringify(nilai));
  } catch (_e) {
    /* penyimpanan diblokir — preferensi berlaku selama sesi saja */
  }
}

export function NavProvider({ userId, children }) {
  const kunci = `etams.nav.v1.${userId || "anon"}`;
  const [prefs, setPrefs] = React.useState(() => bacaPrefs(kunci));
  const [butir, setButir] = React.useState({});      // to -> {to,label,icon,testId,kelompok}
  const [kelompok, setKelompok] = React.useState({}); // testId -> {judul, icon, urut}
  const [cariBuka, setCariBuka] = React.useState(false);

  // Baca ulang hanya bila user BERGANTI (mis. profil baru termuat) — bukan saat pertama pasang: pembacaan
  // pertama sudah dilakukan penginisialisasi, dan membacanya lagi menimpa perubahan dari anak-anaknya.
  const kunciLalu = React.useRef(kunci);
  React.useEffect(() => {
    if (kunciLalu.current === kunci) return;
    kunciLalu.current = kunci;
    setPrefs(bacaPrefs(kunci));
  }, [kunci]);

  const ubahPrefs = React.useCallback((fn) => {
    setPrefs((lama) => {
      const baru = fn(lama);
      tulisPrefs(kunci, baru);
      return baru;
    });
  }, [kunci]);

  const daftar = React.useCallback((b) => {
    setButir((m) => {
      const lama = m[b.to];
      if (lama && lama.label === b.label && lama.kelompok === b.kelompok && lama.testId === b.testId) return m;
      return { ...m, [b.to]: b };
    });
  }, []);
  const cabut = React.useCallback((to, testId) => {
    setButir((m) => {
      if (!m[to] || m[to].testId !== testId) return m;
      const { [to]: _hapus, ...sisa } = m;
      return sisa;
    });
  }, []);
  const daftarKelompok = React.useCallback((id, meta) => {
    setKelompok((m) => (m[id] && m[id].judul === meta.judul ? m : { ...m, [id]: meta }));
  }, []);
  const cabutKelompok = React.useCallback((id) => {
    setKelompok((m) => {
      if (!m[id]) return m;
      const { [id]: _hapus, ...sisa } = m;
      return sisa;
    });
  }, []);

  // Ctrl+K / Cmd+K membuka "Cari menu".
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCariBuka((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const nilai = React.useMemo(() => ({
    prefs, ubahPrefs, butir, daftar, cabut, kelompok, daftarKelompok, cabutKelompok, cariBuka, setCariBuka,
    ciut: !!prefs.ciut,
  }), [prefs, ubahPrefs, butir, daftar, cabut, kelompok, daftarKelompok, cabutKelompok, cariBuka]);
  return <NavCtx.Provider value={nilai}>{children}</NavCtx.Provider>;
}

export function useNav() {
  return React.useContext(NavCtx);
}

function judulKelompokDari(el) {
  const seksi = el && el.closest('[data-testid$="-section"]');
  const kepala = seksi && seksi.querySelector("[data-nav-kepala]");
  return kepala ? kepala.getAttribute("data-judul") : null;
}

/** Butir menu biasa. Mendaftar ke konteks saat DIRENDER (artinya lolos gerbang perannya). */
export function NavItem({ to, icon: Icon, children, testId }) {
  const nav = useNav();
  const ref = React.useRef(null);
  const label = typeof children === "string" ? children.trim() : React.Children.toArray(children).join("").trim();
  React.useLayoutEffect(() => {
    if (!nav) return undefined;
    nav.daftar({ to, label, icon: Icon, testId, kelompok: judulKelompokDari(ref.current) });
    return () => nav.cabut(to, testId);
  }, [nav && nav.daftar, nav && nav.cabut, to, label, Icon, testId]); // eslint-disable-line react-hooks/exhaustive-deps
  const disemat = !!(nav && (nav.prefs.sematan || []).includes(to));
  return (
    <div className="group/butir relative flex items-center">
      <NavLink
        ref={ref}
        to={to}
        data-testid={testId}
        className={({ isActive }) =>
          `flex flex-1 items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] leading-5 transition-colors ${
            isActive
              ? "bg-brand-navy-foreground/15 text-brand-navy-foreground font-medium"
              : "text-brand-navy-foreground/75 hover:bg-brand-navy-foreground/10 hover:text-brand-navy-foreground"
          }`
        }
      >
        <Icon size={16} className="shrink-0" />
        <span className="truncate">{children}</span>
      </NavLink>
      {nav && (
        <button
          type="button"
          className={`absolute right-1 rounded p-1 text-brand-navy-foreground/60 hover:text-brand-navy-foreground ${
            disemat ? "opacity-100" : "opacity-0 group-hover/butir:opacity-100 focus:opacity-100"
          }`}
          title={disemat ? "Lepas sematan" : "Sematkan menu ini"}
          aria-label={disemat ? `Lepas sematan ${label}` : `Sematkan ${label}`}
          data-testid={`sematkan-${testId}`}
          onClick={() => nav.ubahPrefs((p) => {
            const s = p.sematan || [];
            return { ...p, sematan: s.includes(to) ? s.filter((x) => x !== to) : [...s, to] };
          })}
        >
          <Star size={12} fill={disemat ? "currentColor" : "none"} />
        </button>
      )}
    </div>
  );
}

/**
 * Judul kelompok yang bisa dilipat. Ditaruh sebagai anak PERTAMA div seksi yang sudah ada;
 * menyembunyikan saudara-saudaranya lewat CSS (`index.css`: seksi yang kepalanya aria-expanded=false).
 */
export function KepalaKelompok({ icon: Icon, judul, id }) {
  const nav = useNav();
  const { pathname } = useLocation();
  const ref = React.useRef(null);
  const [jumlah, setJumlah] = React.useState(0);
  const tersimpan = nav ? (nav.prefs.buka || {})[id] : undefined;
  const [adaAktif, setAdaAktif] = React.useState(false);
  const aktifLalu = React.useRef(false);
  // Kelompok halaman aktif terbuka otomatis kecuali user menutupnya; kelompok lain mengikuti pilihan
  // yang diingat (bawaan tertutup).
  const buka = adaAktif ? tersimpan !== false : tersimpan === true;

  // Jumlah menu kelompok = butir yang benar-benar tampil (lolos gerbang) di seksi ini; dihitung ulang
  // tiap kali daftar butir berubah.
  const banyakButir = nav ? Object.keys(nav.butir).length : 0;
  React.useLayoutEffect(() => {
    const seksi = ref.current && ref.current.parentElement;
    if (seksi) setJumlah(seksi.querySelectorAll("a[data-testid]").length);
  }, [banyakButir]);

  React.useLayoutEffect(() => {
    if (!nav) return undefined;
    nav.daftarKelompok(id, { judul, icon: Icon });
    return () => nav.cabutKelompok(id);
  }, [nav && nav.daftarKelompok, nav && nav.cabutKelompok, id, judul, Icon]); // eslint-disable-line react-hooks/exhaustive-deps

  // Yang terbuka otomatis hanya kelompok halaman aktif. Tiba DI kelompok ini dari halaman lain
  // menghapus "tutup" yang pernah dipilih, supaya kelompok halaman aktif selalu terlihat saat tiba.
  React.useLayoutEffect(() => {
    const seksi = ref.current && ref.current.parentElement;
    const aktif = !!(seksi && seksi.querySelector('a[aria-current="page"]'));
    if (aktif && !aktifLalu.current && nav && (nav.prefs.buka || {})[id] === false) {
      nav.ubahPrefs((p) => {
        const { [id]: _hapus, ...sisa } = p.buka || {};
        return { ...p, buka: sisa };
      });
    }
    aktifLalu.current = aktif;
    setAdaAktif(aktif);
  }, [pathname, banyakButir]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <button
      ref={ref}
      type="button"
      data-nav-kepala=""
      data-judul={judul}
      aria-expanded={buka}
      data-testid={`kepala-${id}`}
      onClick={() => nav && nav.ubahPrefs((p) => ({ ...p, buka: { ...(p.buka || {}), [id]: !buka } }))}
      className="w-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-navy-foreground/60 hover:text-brand-navy-foreground flex items-center gap-1 select-none"
    >
      <ChevronRight size={12} className={`transition-transform ${buka ? "rotate-90" : ""}`} />
      <Icon size={12} /> <span className="flex-1 text-left">{judul}</span>
      <span className="text-[10px] font-normal normal-case tracking-normal text-brand-navy-foreground/50" data-testid={`jumlah-${id}`}>{jumlah}</span>
    </button>
  );
}

function TautanPendek({ b, awalan }) {
  const Icon = b.icon;
  return (
    <NavLink
      to={b.to}
      data-testid={`${awalan}-${b.testId}`}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] leading-5 ${
          isActive ? "bg-brand-navy-foreground/15 font-medium" : "text-brand-navy-foreground/75 hover:bg-brand-navy-foreground/10"
        }`
      }
    >
      {Icon && <Icon size={16} className="shrink-0" />}
      <span className="truncate">{b.label}</span>
    </NavLink>
  );
}

/** Bagian atas sidebar: tombol Cari menu, Disematkan, Terakhir dibuka. Dicatat "terakhir dibuka" di sini. */
export function NavAtas() {
  const nav = useNav();
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (!nav) return;
    const cocok = Object.values(nav.butir)
      .filter((b) => pathname === b.to || pathname.startsWith(`${b.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0];
    if (!cocok) return;
    nav.ubahPrefs((p) => {
      const lama = (p.terakhir || []).filter((x) => x !== cocok.to);
      return { ...p, terakhir: [cocok.to, ...lama].slice(0, MAKS_TERAKHIR) };
    });
  }, [pathname, nav && Object.keys(nav.butir).length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!nav) return null;
  const sematan = (nav.prefs.sematan || []).map((to) => nav.butir[to]).filter(Boolean);
  const terakhir = (nav.prefs.terakhir || []).map((to) => nav.butir[to]).filter(Boolean);
  return (
    <div className="space-y-2 pb-2 mb-1 border-b border-brand-navy-foreground/10" data-testid="nav-atas">
      <button
        type="button"
        onClick={() => nav.setCariBuka(true)}
        className="w-full flex items-center gap-2 rounded-md bg-brand-navy-foreground/10 px-3 py-1.5 text-[13px] text-brand-navy-foreground/70 hover:text-brand-navy-foreground"
        data-testid="nav-cari-menu"
      >
        <Search size={14} /> <span className="flex-1 text-left">Cari menu</span>
        <kbd className="text-[10px] text-brand-navy-foreground/50">Ctrl K</kbd>
      </button>
      {sematan.length > 0 && (
        <div data-testid="nav-disematkan">
          <div className="px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy-foreground/60 flex items-center gap-1">
            <Star size={11} /> Disematkan
          </div>
          {sematan.map((b) => <TautanPendek key={b.to} b={b} awalan="nav-pin" />)}
        </div>
      )}
      {terakhir.length > 0 && (
        <div data-testid="nav-terakhir">
          <div className="px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy-foreground/60 flex items-center gap-1">
            <Clock size={11} /> Terakhir dibuka
          </div>
          {terakhir.map((b) => <TautanPendek key={b.to} b={b} awalan="nav-recent" />)}
        </div>
      )}
    </div>
  );
}

/** "Cari menu" — hanya menu yang terdaftar (boleh dilihat peran ini); cari nama menu dan nama kelompok. */
export function cocokCari(b, kata) {
  const q = (kata || "").trim().toLowerCase();
  if (!q) return true;
  const teks = `${b.label} ${b.kelompok || ""}`.toLowerCase();
  return q.split(/\s+/).every((k) => teks.includes(k));
}

export function CariMenu() {
  const nav = useNav();
  const navigate = useNavigate();
  const [kata, setKata] = React.useState("");
  React.useEffect(() => { if (nav && !nav.cariBuka) setKata(""); }, [nav && nav.cariBuka]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!nav) return null;
  // Pencarian teks biasa (semua kata harus ada di nama menu atau nama kelompok) — bukan pencocokan
  // fuzzy: "tarif" tidak boleh menaruh "Transfer Antar Cabang" di atas "Tarif Borongan".
  const hasil = Object.values(nav.butir).filter((b) => cocokCari(b, kata));
  const perKelompok = [];
  hasil.forEach((b) => {
    const k = b.kelompok || "";
    let g = perKelompok.find((x) => x.k === k);
    if (!g) { g = { k, isi: [] }; perKelompok.push(g); }
    g.isi.push(b);
  });
  return (
    <Dialog open={nav.cariBuka} onOpenChange={nav.setCariBuka}>
      <DialogContent className="overflow-hidden p-0">
        <DialogTitle className="sr-only">Cari menu</DialogTitle>
        <Command shouldFilter={false}
                 className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2">
          <CommandInput placeholder="Cari menu atau kelompok…" value={kata} onValueChange={setKata}
                        data-testid="cari-menu-input" />
          <CommandList>
            <CommandEmpty>Tidak ada menu yang cocok.</CommandEmpty>
            {perKelompok.map(({ k, isi }) => (
              <CommandGroup key={k || "-"} heading={k || undefined}>
                {isi.map((b) => {
                  const Icon = b.icon;
                  return (
                    <CommandItem
                      key={b.to}
                      value={b.testId}
                      onSelect={() => { nav.setCariBuka(false); navigate(b.to); }}
                      data-testid={`cari-hasil-${b.testId}`}
                    >
                      {Icon && <Icon size={14} className="mr-2" />} {b.label}
                      {k && <span className="ml-auto text-xs text-muted-foreground">{k}</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** Tombol ciut/lebar (Versi A ↔ Versi B), hanya layar md ke atas. */
export function TombolCiut() {
  const nav = useNav();
  if (!nav) return null;
  return (
    <button
      type="button"
      onClick={() => nav.ubahPrefs((p) => ({ ...p, ciut: !p.ciut }))}
      className="hidden md:inline-flex rounded p-1 text-brand-navy-foreground/60 hover:text-brand-navy-foreground"
      title={nav.ciut ? "Lebarkan menu" : "Ciutkan menu"}
      aria-label={nav.ciut ? "Lebarkan menu" : "Ciutkan menu"}
      data-testid="nav-ciut-toggle"
    >
      {nav.ciut ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
    </button>
  );
}

/**
 * Versi B — rail ikon kelompok. Dibangun dari daftar butir yang terdaftar; menu tanpa kelompok
 * (Dashboard, Dashboard Umum) tampil sebagai ikon sendiri. Arahkan kursor atau tap ikon kelompok →
 * daftar menunya muncul di samping.
 */
function PanelRail({ g }) {
  const ref = React.useRef(null);
  const [gaya, setGaya] = React.useState({ visibility: "hidden", top: 0, left: 0 });
  const ukur = React.useCallback(() => {
    const el = ref.current;
    const tombol = el?.parentElement?.getBoundingClientRect();
    if (!el || !tombol) return;
    setGaya(posisiPanel(tombol, el.scrollHeight, el.offsetWidth,
      { tinggi: window.innerHeight, lebar: window.innerWidth }));
  }, []);
  React.useLayoutEffect(() => {
    ukur();
    window.addEventListener("resize", ukur);
    return () => window.removeEventListener("resize", ukur);
  }, [ukur, g.isi.length]);
  return (
    <div ref={ref} style={gaya}
         className="fixed z-50 w-56 overflow-y-auto overscroll-contain rounded-md border border-brand-navy-foreground/15 bg-brand-navy p-1 shadow-lg"
         data-testid={`nav-rail-panel-${g.id}`}>
      <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-navy-foreground/60">{g.judul}</div>
      {g.isi.map((b) => <TautanPendek key={b.to} b={b} awalan="nav-rail" />)}
    </div>
  );
}

export function RailCiut() {
  const nav = useNav();
  const [terbuka, setTerbuka] = React.useState(null);
  const { pathname } = useLocation();
  React.useEffect(() => { setTerbuka(null); }, [pathname]);
  if (!nav) return null;
  const semua = Object.values(nav.butir);
  const tanpaKelompok = semua.filter((b) => !b.kelompok);
  const grup = Object.entries(nav.kelompok)
    .map(([id, m]) => ({ id, ...m, isi: semua.filter((b) => b.kelompok === m.judul) }))
    .filter((g) => g.isi.length > 0);
  return (
    <div className="flex md:flex-col items-center gap-1 p-2" data-testid="nav-rail" onMouseLeave={() => setTerbuka(null)}>
      <button type="button" onClick={() => nav.setCariBuka(true)} title="Cari menu (Ctrl+K)" aria-label="Cari menu"
              className="rounded-md p-2 text-brand-navy-foreground/70 hover:bg-brand-navy-foreground/10" data-testid="nav-rail-cari">
        <Search size={18} />
      </button>
      {tanpaKelompok.map((b) => {
        const Icon = b.icon;
        return (
          <NavLink key={b.to} to={b.to} title={b.label} aria-label={b.label} data-testid={`nav-rail-${b.testId}`}
                   className={({ isActive }) => `rounded-md p-2 ${isActive ? "bg-brand-navy-foreground/15" : "text-brand-navy-foreground/70 hover:bg-brand-navy-foreground/10"}`}>
            <Icon size={18} />
          </NavLink>
        );
      })}
      {grup.map((g) => {
        const Icon = g.icon;
        const aktif = g.isi.some((b) => pathname === b.to || pathname.startsWith(`${b.to}/`));
        return (
          <div key={g.id} className="relative" onMouseEnter={() => setTerbuka(g.id)}>
            <button
              type="button"
              title={g.judul}
              aria-label={g.judul}
              aria-expanded={terbuka === g.id}
              onClick={() => setTerbuka((t) => (t === g.id ? null : g.id))}
              className={`rounded-md p-2 ${aktif ? "bg-brand-navy-foreground/15" : "text-brand-navy-foreground/70 hover:bg-brand-navy-foreground/10"}`}
              data-testid={`nav-rail-kelompok-${g.id}`}
            >
              {Icon && <Icon size={18} />}
            </button>
            {terbuka === g.id && <PanelRail g={g} />}
          </div>
        );
      })}
    </div>
  );
}

/** Tombol panah atas — muncul sesudah digulir jauh; menggulir halus ke paling atas. */
export function TombolKeAtas({ wadahRef }) {
  const [tampil, setTampil] = React.useState(false);
  React.useEffect(() => {
    const el = wadahRef && wadahRef.current;
    const periksa = () => {
      const y = Math.max(el ? el.scrollTop : 0, window.scrollY || 0);
      // T35: tombol tidak boleh menimpa navigasi (sidebar / panel menu Versi B) di ukuran layar mana pun.
      const kotakNavigasi = Array.from(document.querySelectorAll('[data-testid=sidebar], [data-testid^="nav-rail-panel"]'))
        .map((n) => { const r = n.getBoundingClientRect(); return [r.left, r.top, r.right, r.bottom]; });
      setTampil(bolehTampil({ gulir: y, lebar: window.innerWidth, tinggi: window.innerHeight, kotakNavigasi }));
    };
    // capture: gulir di wadah mana pun (isi halaman, daftar menu sidebar) ikut memeriksa ulang.
    document.addEventListener("scroll", periksa, { passive: true, capture: true });
    window.addEventListener("resize", periksa, { passive: true });
    periksa();
    return () => {
      document.removeEventListener("scroll", periksa, { capture: true });
      window.removeEventListener("resize", periksa);
    };
  }, [wadahRef]);
  if (!tampil) return null;
  return (
    <button
      type="button"
      onClick={() => {
        const el = wadahRef && wadahRef.current;
        if (el) el.scrollTo({ top: 0, behavior: "smooth" });
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className="fixed bottom-5 right-5 z-40 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:opacity-90"
      title="Kembali ke atas"
      aria-label="Kembali ke atas"
      data-testid="tombol-ke-atas"
    >
      <ArrowUp size={18} />
    </button>
  );
}
